import { BROWSE_ONLY, PUBLIC_POSTHOG_APP_KEY, PUBLIC_POSTHOG_HOST } from "@/config";

import {
  type Analytics,
  type AnalyticsProperties,
  type AppEvent,
  createAnalytics,
  disabledAnalytics,
} from "./analytics";

let loading: Promise<Analytics> | undefined;

/**
 * The PostHog adapter for the authenticated app's analytics port.
 *
 * Keeping this SDK out of the `BROWSE_ONLY` bundle takes two mechanisms. ADR-0061 requires the
 * identified path to stay "separate from the `BROWSE_ONLY` bundle", and a dynamic `import()` alone
 * does not achieve that: Metro resolves `import()` eagerly for `expo export -p web` and inlines the
 * module, so the SDK still ships in `dist/_expo`. That is why `metro.config.js` also substitutes
 * `posthog-react-native` with a no-op shim whenever `EXPO_PUBLIC_BROWSE_ONLY=1`.
 *
 * The runtime `BROWSE_ONLY` check below stops the adapter running on the browse host, and the Metro
 * alias stops the SDK shipping there. Neither is redundant, and the ADR's requirement needs the
 * alias.
 *
 * The dynamic import stays because it lets the native bundles defer the SDK past first paint, and
 * the isolation test pins it so it is not changed back to a static import.
 *
 * The browse deploy's existing bundle guard would not catch the SDK shipping. It greps for
 * `PostHogPersistence`, a posthog-js class name that appears nowhere in `posthog-react-native` or
 * `@posthog/core`, so it guards the anonymous SDK's placement, not this one's.
 */
export function loadAnalytics(): Promise<Analytics> {
  if (loading) return loading;
  if (BROWSE_ONLY || !PUBLIC_POSTHOG_APP_KEY) {
    loading = Promise.resolve(disabledAnalytics());
    return loading;
  }

  loading = import("posthog-react-native")
    .then(({ default: PostHog }) => {
      // The SDK's own instance type is used directly, with no assertion. Asserting a hand-written
      // structural type would hide any signature drift on the methods below behind a shape this
      // file claims rather than checks, and `oxlint --type-aware` rejects it as unsafe.
      const posthog = new PostHog(PUBLIC_POSTHOG_APP_KEY, {
        host: PUBLIC_POSTHOG_HOST,
        // Application Installed, Updated, Opened and Backgrounded events at no cost. They show
        // which app version a person is on and make the reach of a bad OTA visible.
        captureAppLifecycleEvents: true,
        // Client-side error tracking. Logfire covers the backend; this covers a crash on a
        // Vendor's phone behind a table at a show, the failure most likely to end a session and
        // least likely to be reported.
        errorTracking: {
          autocapture: {
            uncaughtExceptions: true,
            unhandledRejections: true,
            // Console capture stays off; this is the most consequential setting in the block.
            // Console output does not pass through `redactProperties`: it is whatever any call
            // site logged, which in this app includes card names, costs and asks. Turning it on
            // would silently undo the redaction guarantee everywhere at once.
            console: false,
            // Explicit rather than defaulted. True would require `@posthog/react-native-plugin`,
            // a native module: it would move the Expo fingerprint (blocking OTA updates) and break
            // `expo export -p web` at import time (mobile/AGENTS.md). Off keeps this shippable
            // over the air.
            nativeCrashes: false,
          },
        },
        // Native replay needs `posthog-react-native-session-replay`, a native module that breaks
        // `expo export -p web` at import time (mobile/AGENTS.md). Off here and off in the project
        // settings (TRO-631): one control in code and one in the dashboard, because ADR-0061 holds
        // that either one alone is a property nobody re-verifies.
        enableSessionReplay: false,
        // Left at the SDK default rather than set to true, but currently inert: the shared project
        // has "Discard client IP data" on (ADR-0061, TRO-520), so PostHog drops the address before
        // enrichment and derives no location for a signed-in Vendor either. That is an accepted
        // cost of the single-project arrangement, because shop regions can be answered from
        // account data. Only splitting the projects would make this setting take effect.
        disableGeoip: false,
      });

      return createAnalytics({
        capture(event, properties) {
          posthog.capture(event, properties);
        },
        screen(routeName, properties) {
          void posthog.screen(routeName, properties);
        },
        identify(distinctId, properties) {
          posthog.identify(distinctId, properties);
        },
        reportError(error, properties) {
          posthog.captureException(error, properties);
        },
        setOnce(properties) {
          // The second argument is `$set_once`: PostHog itself refuses to overwrite these, so the
          // guarantee holds against a bug in this app as well as a later visit.
          posthog.setPersonProperties(undefined, properties);
        },
        group(groupType, groupKey, properties) {
          posthog.group(groupType, groupKey, properties);
        },
        reset() {
          posthog.reset();
        },
      });
    })
    .catch(() => disabledAnalytics());

  return loading;
}

/** Tests mount and unmount the bridge repeatedly; without this the first run's promise is reused
 * and the second run asserts against the first run's sink. */
export function resetAnalyticsForTests(): void {
  loading = undefined;
}

/**
 * Fire-and-forget emit for a call site inside a screen.
 *
 * Screens should never await analytics or hold the client in state: the adapter loads
 * asynchronously, and a scan or a ring-up must not wait on it or fail because of it. This resolves
 * the client, emits, and swallows every error. `loadAnalytics` already falls back to the no-op
 * sink, so this catch is the last layer of what ADR-0061 requires: analytics failure never blocks
 * a surface.
 */
export function trackEvent(event: AppEvent, properties?: AnalyticsProperties): void {
  void loadAnalytics()
    .then((analytics) => analytics.track(event, properties))
    .catch(() => {});
}

/**
 * Fire-and-forget `$screen` for a view that sends no route change, such as the Dossier shown in a
 * detail pane. `AnalyticsBridge` sends every route's own.
 */
export function trackScreen(routeName: string, properties?: AnalyticsProperties): void {
  void loadAnalytics()
    .then((analytics) => analytics.screen(routeName, properties))
    .catch(() => {});
}
